import { useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Context } from "../../context/context";
import { Button, Card } from "../../components/ui";
import { WifiIcon } from "../../components/ui/icons";

const DEVICE_SETUP_URL = "http://192.168.4.1";

/**
 * Instructions for joining a device's setup network and handing off to its
 * own setup screen. Reached two ways — with no query param, for a device
 * that's never been added to the account yet (linked from the Devices
 * page); or with ?deviceId=..., to reconfigure a device the account already
 * has (linked from Device Settings). Same underlying steps either way — the
 * device doesn't care which context you came from — so this is one page,
 * not two.
 *
 * Deliberately does not talk to the device itself: this page is served over
 * HTTPS, and the device's setup API only exists at a private, plain-HTTP
 * address reachable while joined to its network — browsers block that kind
 * of request from an HTTPS page outright. The device's own setup screen
 * (opened via the button below) is what actually does the work.
 */
const WifiSetup = () => {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get("deviceId");
  const { state } = useContext(Context);
  const navigate = useNavigate();

  const nickname = deviceId && state.user?.nicknames?.[deviceId];

  return (
    <>
      <p className="mb-5 text-sm text-gray-500">
        {deviceId
          ? `Reconfigure the WiFi for ${nickname || deviceId}.`
          : "Connect a new device to your home WiFi before adding it to your account."}
      </p>

      <Card className="max-w-lg p-5 nav:p-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <WifiIcon className="h-5 w-5" />
          </span>
          <p className="text-sm font-bold text-primary-900">
            {deviceId ? "Reconfigure WiFi" : "Connect your device"}
          </p>
        </div>

        <ol className="list-decimal space-y-3 pl-5 text-sm text-gray-700">
          {deviceId ? (
            <li>
              Press and hold the button on top of your device for about 5 seconds, until the light turns amber. This
              clears its current WiFi.
            </li>
          ) : (
            <li>Turn on your device.</li>
          )}
          <li>
            On your phone or computer, open WiFi settings and join the network named{" "}
            {deviceId ? (
              <span className="font-mono font-semibold text-primary-900">{deviceId}</span>
            ) : (
              "the same number printed on the bottom of your device"
            )}
            . No password needed.
          </li>
          <li>A setup screen should open on its own. If it doesn't, use the button below.</li>
          <li>Choose your home WiFi and enter its password.</li>
          <li>When it says "Connected," come back to this page.</li>
        </ol>

        <Button as="a" href={DEVICE_SETUP_URL} target="_blank" rel="noreferrer" className="mt-5 w-full nav:w-auto">
          Continue to device setup
        </Button>
        <p className="mt-2 text-xs text-gray-500">Only works while you're joined to your device's WiFi network.</p>
      </Card>

      <button
        onClick={() => navigate(deviceId ? `/device/${deviceId}/settings` : "/")}
        className="mt-5 text-xs font-semibold text-primary-600 hover:underline"
      >
        {deviceId ? "Back to device settings" : "Back to my devices"}
      </button>
    </>
  );
};

export default WifiSetup;
